import { useState } from "react";
import { arktypeResolver } from "@hookform/resolvers/arktype";
import { useForm } from "react-hook-form";

import { ExamSchemas } from "@/api/router/exams/schemas";
import {
	useExamArbitrateDate,
	useExamConfirmIdentity,
	useExamEditInterpretation,
	useExamReanalyze,
} from "@/hooks/use-exams";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { OverlayShell } from "@/components/ui/overlay";
import { format } from "@/lib/format";
import { ExamLabels } from "@/lib/labels/exams";
import { Button } from "@/components/ui/button";
import type { ExamsPivot } from "@/api/db/exams";

function collectionDateLabel(value: string | null) {
	if (value === null) {
		return "Sem data";
	}

	return format.localDate(value);
}

function validationLabel(validatedBy: string | null) {
	if (validatedBy === null) {
		return "Não validado";
	}

	return `Validado por ${validatedBy}`;
}

function DocumentRow({ document }: { document: ExamsPivot["documents"][number] }) {
	const reanalyze = useExamReanalyze();
	const confirmIdentity = useExamConfirmIdentity();
	const arbitrateDate = useExamArbitrateDate();
	const [collectionDate, setCollectionDate] = useState<string | null>(null);

	return (
		<div className="flex flex-col gap-2 rounded-md border border-border px-3 py-2 text-sm">
			<div className="flex items-center justify-between gap-3">
				<div className="flex min-w-0 flex-col">
					<span className="truncate text-foreground">
						{document.laboratory ?? "Laboratório não identificado"}
					</span>
					<span className="text-xs text-muted-foreground">
						{collectionDateLabel(document.collectionDate)}
					</span>
				</div>

				<div className="flex shrink-0 items-center gap-3">
					<span className="text-xs uppercase tracking-wide text-muted-foreground">
						{ExamLabels.status(document.status)}
					</span>
					<a
						className="text-xs text-accent-foreground underline-offset-2 hover:underline"
						href={`/exams/file/${document.id}`}
						target="_blank"
						rel="noreferrer"
					>
						abrir
					</a>
				</div>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					disabled={reanalyze.isPending}
					onClick={() => reanalyze.mutate({ document_id: document.id })}
					className="control-button button-outline min-h-11 px-3 py-2 text-xs md:min-h-9"
				>
					{reanalyze.isPending && "reanalisando exame..."}
					{!reanalyze.isPending && "reanalisar exame"}
				</button>

				{document.status === "identity_blocked" && (
					<ConfirmationDialog
						slot="confirm-identity-trigger"
						tone="destructive"
						title={ExamLabels.confirmIdentity.prompt}
						pendingLabel={ExamLabels.confirmIdentity.pending}
						isPending={confirmIdentity.isPending}
						onConfirm={() => confirmIdentity.mutate({ document_id: document.id })}
						className="rounded-full border border-destructive/50 px-2 py-0.5 text-xs text-destructive hover:bg-destructive/10 disabled:opacity-50"
					>
						{ExamLabels.confirmIdentity.trigger}
					</ConfirmationDialog>
				)}

				{document.status === "divergent" && (
					<div className="flex items-center gap-2">
						<DatePicker
							slot="collection-date"
							value={collectionDate}
							onChange={setCollectionDate}
							placeholder="Data da coleta"
							maxDate={new Date()}
						/>

						<button
							type="button"
							disabled={arbitrateDate.isPending || collectionDate === null}
							onClick={() => {
								if (collectionDate === null) {
									return;
								}

								arbitrateDate.mutate({
									document_id: document.id,
									collection_date: collectionDate,
								});
							}}
							className="control-button button-outline min-h-11 px-3 py-2 text-xs md:min-h-9"
						>
							{arbitrateDate.isPending && "definindo data..."}
							{!arbitrateDate.isPending && "definir data"}
						</button>
					</div>
				)}
			</div>
		</div>
	);
}

export function Documents({ documents }: { documents: ExamsPivot["documents"] }) {
	if (documents.length === 0) {
		return null;
	}

	return (
		<div className="flex flex-col gap-2">
			<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
				Documentos
			</p>

			{documents.map((document) => (
				<DocumentRow key={document.id} document={document} />
			))}
		</div>
	);
}

export function Reports({ reports }: { reports: ExamsPivot["reports"] }) {
	if (reports.length === 0) {
		return null;
	}

	return (
		<div className="flex flex-col gap-2">
			<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
				Laudos descritivos
			</p>

			{reports.map((report) => (
				<div key={report.id} className="rounded-md border border-border px-3 py-2 text-sm">
					{!!report.conclusion && <p className="text-foreground">{report.conclusion}</p>}
					{!!report.findings && <p className="text-muted-foreground">{report.findings}</p>}
				</div>
			))}
		</div>
	);
}

function Interpretation({ item }: { item: ExamsPivot["interpretations"][number] }) {
	const [editing, setEditing] = useState(false);

	return (
		<div className="flex flex-col gap-2 rounded-md border border-border px-3 py-2 text-sm">
			<p className="whitespace-pre-wrap text-foreground">{item.text}</p>

			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-xs text-muted-foreground">{validationLabel(item.validatedBy)}</span>

				<Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)}>
					Editar interpretação
				</Button>
			</div>

			{editing && <InterpretationModal item={item} onClose={() => setEditing(false)} />}
		</div>
	);
}

function InterpretationModal({
	item,
	onClose,
}: {
	item: ExamsPivot["interpretations"][number];
	onClose: () => void;
}) {
	const edit = useExamEditInterpretation();
	const [pendingAction, setPendingAction] = useState<"save" | "validate" | null>(null);
	const {
		register,
		handleSubmit,
		formState: { isValid },
	} = useForm<{ text: string }>({
		resolver: arktypeResolver(ExamSchemas.editInterpretation.pick("text"), undefined, {
			raw: true,
		}),
		mode: "onChange",
		defaultValues: { text: item.text },
	});
	const save = handleSubmit(({ text }) => {
		setPendingAction("save");
		edit.mutate(
			{ document_id: item.documentId, text, validate: false },
			{ onSuccess: onClose, onSettled: () => setPendingAction(null) },
		);
	});
	const validate = handleSubmit(({ text }) => {
		setPendingAction("validate");
		edit.mutate(
			{ document_id: item.documentId, text, validate: true },
			{ onSuccess: onClose, onSettled: () => setPendingAction(null) },
		);
	});

	return (
		<OverlayShell
			label="Editar interpretação"
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<form onSubmit={save} className="overlay-panel max-w-xl gap-4 p-5">
				<h2 className="type-section-title text-foreground">Editar interpretação</h2>

				<textarea
					{...register("text")}
					autoFocus
					onKeyDown={(event) => {
						if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
							event.preventDefault();
							void save();
						}
					}}
					rows={7}
					className="control-field min-h-24 resize-y"
				/>

				<div className="flex flex-wrap items-center justify-end gap-2">
					<Button
						type="button"
						variant="secondary"
						size="sm"
						disabled={edit.isPending}
						onClick={onClose}
					>
						Cancelar
					</Button>

					<Button type="submit" variant="outline" size="sm" disabled={edit.isPending || !isValid}>
						{pendingAction === "save" && "salvando interpretação..."}
						{pendingAction !== "save" && "Salvar"}
					</Button>

					<Button
						type="button"
						variant="default"
						size="sm"
						disabled={edit.isPending || !isValid}
						onClick={() => void validate()}
					>
						{pendingAction === "validate" && "validando interpretação..."}
						{pendingAction !== "validate" && "Salvar e validar"}
					</Button>
				</div>
			</form>
		</OverlayShell>
	);
}

export function Interpretations({ items }: { items: ExamsPivot["interpretations"] }) {
	if (items.length === 0) {
		return null;
	}

	return (
		<div className="flex flex-col gap-2">
			<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
				Interpretação (rascunho)
			</p>

			{items.map((item) => (
				<Interpretation key={item.documentId} item={item} />
			))}
		</div>
	);
}
